import { Button } from "newui/components/Button";
import { TabHeader } from "components/TabHeader";
import { navigate } from "@reach/router";
import { Icon } from "assets/Icon";
import { Flex, HStack, VStack } from "@chakra-ui/react";
import TextButton from "newui/components/Button/TextButton";
import {
	BackButtonProps,
	RfxHeaderButtonProps,
} from "../types/CreateRfxPageTypes";
import ContinueButton from "./useContinueButton";
import { useCurrentUserHasRFxPreApproval } from "hooks/featureFlags";
import { RFX_STATUS } from "utils/constants";
import { IsEqualIgnoreCase } from "helpers/stringHelper";
import { useCurrentUserHasRole } from "hooks/auth";
import { USER_ROLE_NAMES } from "newui/features/Auth/constants/UserRoleConstants";
import { doesRFxHaveApprovers, isRfxApproved } from "helpers/rfxHelper";
import StatusPill from "components/StatusPill";
import FloatingButton from "newui/features/Rfx/components/FloatingButton";

const BackButton: React.FC<BackButtonProps> = ({
	isNew,
	company_id,
	isEdit,
	isTemplate,
}) => {
	return (
		// @ts-ignore
		<Button
			btntype={"icon"}
			onClick={() => {
				if (isTemplate && isEdit) {
					navigate(`/account/${company_id}/manage-templates`);
				} else {
					navigate("..");
				}
			}}
			className={isNew && "!pointer-events-none"}
		>
			{isNew ? (
				<Icon
					icon={"close"}
					style={{
						width: "25px",
						height: "25px",
						marginRight: "0px",
						rotate: "45deg",
					}}
				/>
			) : (
				<Icon
					icon={"flip-backward"}
					style={{ width: "25px", height: "25px", marginRight: "0px" }}
				/>
			)}
		</Button>
	);
};

export const CreateRFxHeaderButtons: React.FC<RfxHeaderButtonProps> = ({
	company_id,
	disableLastStep,
	handleSubmitOutside,
	isEdit,
	isLoading,
	isFileUploading,
	isSubmittingForm,
	isTemplate,
	isTemplateQuoteFormInvalid,
	isValidForm,
	lastStep,
	onOpen,
	onOpenApproval,
	rfqData,
}) => {
	const usePreApprovalFeature = useCurrentUserHasRFxPreApproval();
	const isApproved = isRfxApproved(rfqData);
	const hasApprovers = doesRFxHaveApprovers(rfqData);
	const isPendingApproval =
		usePreApprovalFeature && !isApproved && hasApprovers;

	const showApproval =
		usePreApprovalFeature &&
		IsEqualIgnoreCase(rfqData?.status, RFX_STATUS.DRAFT) &&
		rfqData?.rfxType;
	const userIsAdmin = useCurrentUserHasRole(USER_ROLE_NAMES.COMPANY_ADMIN);

	const isCorrectStatus = !isTemplate && showApproval;
	const showInviteApproversButton =
		isCorrectStatus && (isPendingApproval || !hasApprovers);
	const showIgnoreApproversButton =
		isCorrectStatus && userIsAdmin && isPendingApproval;
	const showApprovalStatusPill = isCorrectStatus && hasApprovers && isApproved;

	const handleSaveProgress = () => {
		handleSubmitOutside("save");
	};

	const isSubmitDisabled = lastStep
		? !isValidForm ||
			disableLastStep ||
			isSubmittingForm ||
			isTemplateQuoteFormInvalid ||
			isFileUploading
		: isSubmittingForm || isFileUploading;

	return (
		<TabHeader
			icon={
				<BackButton
					isNew={!isEdit}
					company_id={company_id}
					isEdit={isEdit}
					isTemplate={isTemplate}
				/>
			}
			heading={
				isTemplate
					? isEdit
						? `Edit ${rfqData?.name || "RFx Template"}`
						: "Create RFx Template"
					: isEdit
						? `Edit ${rfqData?.name || "RFx"}`
						: "CREATE YOUR RFx"
			}
			isLoading={isLoading && isEdit}
			description={rfqData?.project?.name && `Project: ${rfqData.project.name}`}
		>
			<VStack gap={0}>
				{!isLoading && (
					<HStack
						gap={3}
						align={"center"}
					>
						{!isPendingApproval && !isTemplate && (
							<TextButton
								disabled={isSubmittingForm || isFileUploading}
								onClick={handleSaveProgress}
								data-testid="save-button"
							>
								Save Draft
							</TextButton>
						)}

						{!isPendingApproval && (
							<Button
								btntype={"secondary"}
								type="button"
								disabled={isSubmittingForm}
								onClick={onOpen}
								data-testid="preview-button"
							>
								<Flex
									gap={2}
									align={"center"}
								>
									<Icon icon={"view"} />
									Preview
								</Flex>
							</Button>
						)}

						{
							<ContinueButton
								onClick={() =>
									handleSubmitOutside(lastStep ? "laststep" : "next")
								}
								disabled={isSubmitDisabled}
								lastStep={lastStep}
								isTemplate={isTemplate}
								showIgnoreApproversButton={showIgnoreApproversButton}
							/>
						}

						{showApproval && !isTemplate && showInviteApproversButton && (
							<Button
								btntype={"secondary"}
								type="button"
								disabled={isSubmittingForm}
								onClick={onOpenApproval}
								data-testid="approval-button"
							>
								<Flex
									gap={2}
									align={"center"}
								>
									{isPendingApproval
										? "Invite more Approvers"
										: "Send for Approval"}
									<Icon
										icon={"check-verified"}
										className="text-bg-brand-solid"
									/>
								</Flex>
							</Button>
						)}
					</HStack>
				)}
				{showApprovalStatusPill && (
					<StatusPill
						className="mt-5"
						backgroundColor="bg-green-300"
						textColor="text-green-800"
					>
						This RFx has been approved for submission
					</StatusPill>
				)}
				<FloatingButton
					onClick={handleSaveProgress}
					position="left"
					text="Save Progress"
					isLoading={isSubmittingForm || isFileUploading}
					isDisabled={isSubmittingForm || isFileUploading}
					className="bottom-4"
				/>
			</VStack>
		</TabHeader>
	);
};
